import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download, Copy, Check, Share2, MessageCircle } from 'lucide-react';
import { BACKEND_URL } from '@/config';

const API = BACKEND_URL + '/api';

/**
 * QrCodesModal — les deux codes de l'expéditeur, seul à les détenir avec l'admin (T12).
 * Prise en charge : montré au transporteur à la remise. Livraison : transmis au destinataire par un lien à partager
 * (décision du 2026-10-17, option A) — jamais collé sur le colis : quiconque tient le colis pourrait confirmer.
 */
export default function QrCodesModal({ missionId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/qr/mission/${missionId}`).then(r => {
      if (!cancelled) { setData(r.data); setLoading(false); }
    }).catch(err => {
      if (!cancelled) { setError(err?.response?.data?.detail || 'Erreur'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [missionId]);

  if (loading) {
    return (
      <div className="qr-modal-bg" onClick={onClose}>
        <div className="qr-modal" onClick={e => e.stopPropagation()}>
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(14,27,51,0.6)' }}>Chargement...</div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="qr-modal-bg" onClick={onClose}>
        <div className="qr-modal" onClick={e => e.stopPropagation()}>
          <div style={{ padding: 40, textAlign: 'center', color: '#DC2626' }}>{error}</div>
          <div style={{ padding: '0 24px 24px', textAlign: 'right' }}>
            <button className="btn btn-ghost" onClick={onClose}>Fermer</button>
          </div>
        </div>
      </div>
    );
  }

  const origin = window.location.origin;
  const pickupUrl = `${origin}/verify/${data.pickup_token}`;
  const deliveryUrl = `${origin}/verify/${data.delivery_token}`;

  const copy = (label, val) => { navigator.clipboard.writeText(val); setCopied(label); setTimeout(() => setCopied(''), 2000); };
  const shareText = `Colis2Ship — à la réception de votre colis, confirmez la livraison ici : ${deliveryUrl}`;
  const shareDelivery = async () => {
    try {
      await navigator.share({ title: 'Confirmer la réception de votre colis', text: shareText });
    } catch { /* partage annulé */ }
  };
  const downloadSvg = (id, label) => {
    const svg = document.getElementById(id);
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${label}-${missionId.slice(-8)}.svg`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="qr-modal-bg" data-testid="qr-modal" onClick={onClose}>
      <div className="qr-modal" onClick={e => e.stopPropagation()}>
        <div className="qr-modal-header">
          <div>
            <div className="qr-modal-title">Preuves cryptographiques de remise</div>
            <div className="qr-modal-sub">QR signés HMAC-SHA256 · anti-litige</div>
          </div>
          <button className="qr-modal-close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
        </div>
        <div className="qr-modal-body">
          <div className="qr-cards">
            {/* PICKUP QR */}
            <div className={`qr-card qr-card--pickup ${data.pickup_verified_at ? 'is-done' : ''}`}>
              <div className="qr-card-head">
                <span className="qr-step-badge qr-step-badge--gold">1 · Prise en charge</span>
                {data.pickup_verified_at && <span className="qr-status-done"><Check size={11} /> Validé</span>}
              </div>
              <p className="qr-card-desc">
                Affiche ce code au transporteur <strong>au moment de la remise</strong> de ton colis.
                Il le scannera pour prouver la prise en charge.
              </p>
              <div className="qr-svg-wrap" data-testid="qr-pickup">
                <QRCodeSVG
                  id="qr-pickup-svg"
                  value={pickupUrl}
                  size={208}
                  level="H"
                  includeMargin={false}
                  fgColor="#0E1B33"
                  bgColor="#ffffff"
                  imageSettings={{
                    src: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23D4A14A'><rect width='24' height='24' rx='5'/><text x='12' y='17' font-family='Arial' font-weight='900' font-size='14' fill='%230E1B33' text-anchor='middle'>2S</text></svg>",
                    height: 36, width: 36, excavate: true,
                  }}
                />
              </div>
              <div className="qr-actions">
                <button className="qr-btn" onClick={() => downloadSvg('qr-pickup-svg', 'pickup')}><Download size={12} /> Télécharger</button>
                <button className="qr-btn" onClick={() => copy('pickup', pickupUrl)}>{copied === 'pickup' ? <><Check size={12}/> Copié</> : <><Copy size={12}/> Copier URL</>}</button>
              </div>
            </div>

            {/* DELIVERY QR */}
            <div className={`qr-card qr-card--delivery ${data.delivery_verified_at ? 'is-done' : ''}`}>
              <div className="qr-card-head">
                <span className="qr-step-badge qr-step-badge--teal">2 · Remise au destinataire</span>
                {data.delivery_verified_at && <span className="qr-status-done"><Check size={11} /> Validé</span>}
              </div>
              <p className="qr-card-desc">
                <strong>Envoie ce lien au destinataire</strong> (WhatsApp, SMS…). À la réception, il confirme la
                livraison en l'ouvrant, <strong>sans compte</strong>, ou montre ce QR au transporteur.
                Ne le colle pas sur le colis : quiconque tient le colis pourrait confirmer.
              </p>
              <div className="qr-svg-wrap" data-testid="qr-delivery">
                <QRCodeSVG
                  id="qr-delivery-svg"
                  value={deliveryUrl}
                  size={208}
                  level="H"
                  includeMargin={false}
                  fgColor="#0E1B33"
                  bgColor="#ffffff"
                  imageSettings={{
                    src: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%235BAFAF'><rect width='24' height='24' rx='5'/><text x='12' y='17' font-family='Arial' font-weight='900' font-size='14' fill='%23ffffff' text-anchor='middle'>2S</text></svg>",
                    height: 36, width: 36, excavate: true,
                  }}
                />
              </div>
              <div className="qr-actions">
                {typeof navigator.share === 'function' && (
                  <button className="qr-btn" onClick={shareDelivery} data-testid="qr-share-delivery"><Share2 size={12} /> Envoyer au destinataire</button>
                )}
                <a className="qr-btn" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer"
                  data-testid="qr-whatsapp-delivery"><MessageCircle size={12} /> WhatsApp</a>
                <button className="qr-btn" onClick={() => copy('delivery', deliveryUrl)} data-testid="qr-copy-delivery">{copied === 'delivery' ? <><Check size={12}/> Copié</> : <><Copy size={12}/> Copier le lien</>}</button>
              </div>
            </div>
          </div>

          <div className="qr-secure">
            🔒 Chaque QR est signé cryptographiquement. Tout scan reste tracé dans l'historique de mission, et chaque code est à usage unique pour chaque étape (anti-fraude).
          </div>
        </div>
      </div>
    </div>
  );
}
